import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Flex, Spinner, Stack, Text } from '@chakra-ui/react'

import { LOGGED_IN_KEY } from '~constants/localStorage'
import { DASHBOARD_ROUTE, LOGIN_ROUTE } from '~constants/routes'
import { useLocalStorage } from '~hooks/useLocalStorage'
import { useToast } from '~hooks/useToast'
import { ApiService } from '~services/ApiService'

export const BOTMD_SSO_CALLBACK_ENDPOINT = '/auth/botmd/callback'

/**
 * Landing page for the Bot MD SSO handoff. nova redirects the browser here
 * with a short-lived token in the query string; this page exchanges it for a
 * FormSG session via a same-site XHR, then marks the client as logged in.
 */
export const BotmdSsoCallbackPage = (): JSX.Element => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [, setIsAuthenticated] = useLocalStorage<boolean>(LOGGED_IN_KEY)
  const toast = useToast({ isClosable: true, status: 'danger' })
  const hasHandledToken = useRef(false)

  useEffect(() => {
    if (hasHandledToken.current) return
    hasHandledToken.current = true

    const token = searchParams.get('token')
    // Strip the token from the URL immediately so it does not sit in the
    // address bar or the browser back-stack while the exchange is in flight.
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname,
    )

    if (!token) {
      navigate(LOGIN_ROUTE, { replace: true })
      return
    }

    ApiService.post<void>(BOTMD_SSO_CALLBACK_ENDPOINT, { token })
      .then(() => {
        setIsAuthenticated(true)
        navigate(DASHBOARD_ROUTE, { replace: true })
      })
      .catch(() => {
        toast({
          description:
            'Your Bot MD login link is invalid or has expired. Please log in again.',
        })
        navigate(LOGIN_ROUTE, { replace: true })
      })
  }, [navigate, searchParams, setIsAuthenticated, toast])

  return (
    <Flex flex={1} justify="center" align="center" background="primary.100">
      <Stack align="center" spacing="1rem">
        <Spinner color="primary.500" />
        <Text textStyle="subhead-1" color="secondary.500">
          Logging you in...
        </Text>
      </Stack>
    </Flex>
  )
}
